/**
 * VirtualizedDayList - Virtual scrolling container for calendar days
 * Uses react-window's VariableSizeList for efficient rendering of large month views
 */

import React, { forwardRef, useRef, useImperativeHandle, useCallback, useMemo, useEffect } from 'react';
import { VariableSizeList } from 'react-window';
import { format, isSameMonth, startOfMonth } from 'date-fns';
import { useVirtualization, useOverscanCount } from '@/hooks/calendar/useVirtualization';
import { MONTH_DIVIDER_HEIGHT_PX, MONTH_DIVIDER_VISUAL_HEIGHT_PX, MONTH_DIVIDER_LINE_OFFSET_PX } from '@/components/shifts/constants';
import { DayShift, Employee, DayTemplate, HourRange, BusinessClosedDate } from '@/types';
import { Holiday } from '@/lib/scheduling/holidays-simple';
import { ShiftRow } from '@/lib/scheduling/shift-utils';
import VirtualizedDayItem from './VirtualizedDayItem';

interface DayRowCalculation {
  dayDate: Date;
  dayOfMonth: number;
  dailyShifts: DayShift[];
  organizedShiftRows: ShiftRow[];
  numRows: number;
  hasDroppedTemplate: boolean;
  hasShifts: boolean;
}

export interface VirtualizedDayListRef {
  scrollToToday: () => void;
  scrollToDate: (date: Date, align?: 'start' | 'center' | 'end') => void;
  getCurrentDayIndex: () => number;
}

interface VirtualizedDayListProps {
  // Day data
  daysInSelectedMonth: Date[];
  dayRowCalculations: DayRowCalculation[];
  dayRowOffsets: { [key: number]: number };

  // Shift data
  shifts: DayShift[];
  shiftsByDay: { [key: number]: DayShift[] };
  shiftsByDayOrganized: { [key: number]: ShiftRow[] };

  // Infinite scroll support
  onVisibleRangeChange?: (visibleStartIndex: number, visibleStopIndex: number) => void;
  /** Callback when the visible month changes (for sticky month indicator) */
  onVisibleMonthChange?: (month: Date) => void;
  /** Callback when sticky month push offset changes (for iOS-style push effect) */
  onStickyMonthUpdate?: (offsetY: number) => void;
  /** Callback to check if a specific day index is loading (for skeleton display) */
  isDayLoading?: (dayIndex: number) => boolean;
  /** Estimated height for days that haven't loaded yet (default: ROW_HEIGHT_PX) */
  estimatedDayHeight?: number;

  // Employee data
  employees: Employee[];

  // Display configuration
  totalDisplayHours: number;
  effectiveDisplayStartTime: Date;
  effectiveDisplayEndTime: Date;
  hourCellWidthPx: number;
  gridTemplateRows: string;
  gridTemplateColumns: string;

  // Business context
  businessId?: string | null;
  countryCode?: string;
  openingHoursByDayOfWeek: Map<number, any>;
  closedDatesMap?: Map<string, BusinessClosedDate>;
  workingHours: HourRange[];

  // UI state
  hideDayColumn?: boolean;
  hideActionsColumn?: boolean;
  isEditingMode?: boolean;
  show24Hours?: boolean;
  expandedDayKeys: string[];
  selectedEditingDate?: Date | null;

  // Event handlers
  onDaySelect?: (date: Date) => void;
  onAssignEmployee?: (shiftId: string, employeeId: string | null) => void;
  onCreateShift?: (businessId: string, startTime: Date, endTime: Date, title: string) => Promise<void>;
  onRepositionShift?: (shiftId: string, newStartTime: Date, newEndTime: Date) => Promise<void>;
  onOptimisticPositionUpdate?: (shiftId: string, newStartTime: Date, newEndTime: Date) => void;
  onOptimisticPositionClear?: (shiftId: string) => void;
  onShiftSelect?: (shiftId: string, selected: boolean) => void;
  onDeleteShift?: (shiftId: string) => void;
  selectedShiftIds?: Set<string>;
  actionsPanelMode?: string;
  onOpenActionsPanel?: () => void;
  onSetActionsPanelMode?: (mode: string) => void;
  isActionsPanelOpen?: boolean;

  // Template handlers
  onTemplateCreationClick?: (cellTime: Date, event: React.MouseEvent, rowIndex?: number, dayIndex?: number) => void;
  onTemplateCreationHover?: (cellTime: Date, rowIndex?: number, dayIndex?: number) => void;
  onOpenInlineBuilder?: (cellTime: Date, event: React.MouseEvent, rowIndex?: number, dayIndex?: number) => void;
  isTemplateCreationInProgress?: boolean;
  onDayTemplateDrop?: (template: DayTemplate, targetDate: Date) => void;

  // Shift creation overlay state
  templateCreationStartTime?: Date | null;
  templateCreationEndTime?: Date | null;
  templateCreationCursorTime?: Date | null;
  templateCreationDayIndex?: number | null;
  templateCreationRowIndex?: number | null;
  isResizeMode?: boolean;
  onTemplateResizeComplete?: (startTime: Date, endTime: Date) => void;
  onTemplateFinalizedClick?: () => void;

  // Holiday check function
  checkIsHoliday?: (date: Date) => Holiday | null;

  // Time functions - simplified to avoid callback instability
  currentTimeHours?: number;
  currentTimeMinutes?: number;
  getDisplayTimesForDay?: (dayDate: Date, openingHoursByDayOfWeek: Map<number, any>) => {
    startTime: Date;
    endTime: Date;
    totalHours: number;
  };

  // Day validation status
  getDayStatusByKey?: (dateKey: string) => { status: string; [key: string]: any } | null;

  // Events
  showEvents?: boolean;
  eventPositionsWithLayout?: { [day: number]: { [eventId: string]: any } };
  eventsLookup?: Map<string, any>;

  // Dimensions
  containerHeight: number; // Height of the scrollable container
  scrollContainerRef?: React.RefObject<HTMLDivElement>;

  // Historical edit mode - triggers re-render when toggled
  isHistoricalEditEnabled?: boolean;

  // Hide status indicators (day borders)
  hideStatusIndicators?: boolean;
}

const VirtualizedDayList = forwardRef<VirtualizedDayListRef, VirtualizedDayListProps>(
  (props, ref) => {
    const {
      daysInSelectedMonth,
      dayRowCalculations,
      containerHeight,
      onVisibleRangeChange,
      onVisibleMonthChange,
      onStickyMonthUpdate,
      isDayLoading,
      estimatedDayHeight,
    } = props;

    const listRef = useRef<VariableSizeList>(null);
    const overscanCount = useOverscanCount(containerHeight);

    // **CURSOR FOLLOW FIX**: Track cursor time changes to force list updates
    const prevCursorTimeRef = useRef<number | undefined>();
    useEffect(() => {
      const currentCursorTime = props.templateCreationCursorTime?.getTime();

      if (currentCursorTime !== prevCursorTimeRef.current && props.templateCreationDayIndex !== null) {
        // Force react-window to re-render the affected day
        listRef.current?.resetAfterIndex(props.templateCreationDayIndex, false);
        prevCursorTimeRef.current = currentCursorTime;
      }
    }, [props.templateCreationCursorTime, props.templateCreationDayIndex]);

    // Memoize itemProps to prevent re-renders when only currentTime updates
    const itemProps = useMemo(() => {
      return {
      // Include dayRowCalculations for month boundary detection
      dayRowCalculations,
      dayRowOffsets: props.dayRowOffsets,
      shifts: props.shifts,
      shiftsByDay: props.shiftsByDay,
      shiftsByDayOrganized: props.shiftsByDayOrganized,
      employees: props.employees,
      totalDisplayHours: props.totalDisplayHours,
      effectiveDisplayStartTime: props.effectiveDisplayStartTime,
      effectiveDisplayEndTime: props.effectiveDisplayEndTime,
      hourCellWidthPx: props.hourCellWidthPx,
      gridTemplateRows: props.gridTemplateRows,
      gridTemplateColumns: props.gridTemplateColumns,
      businessId: props.businessId,
      countryCode: props.countryCode,
      openingHoursByDayOfWeek: props.openingHoursByDayOfWeek,
      closedDatesMap: props.closedDatesMap,
      workingHours: props.workingHours,
      hideDayColumn: props.hideDayColumn,
      hideActionsColumn: props.hideActionsColumn,
      isEditingMode: props.isEditingMode,
      show24Hours: props.show24Hours,
      expandedDayKeys: props.expandedDayKeys,
      selectedEditingDate: props.selectedEditingDate,
      onDaySelect: props.onDaySelect,
      onAssignEmployee: props.onAssignEmployee,
      onCreateShift: props.onCreateShift,
      onRepositionShift: props.onRepositionShift,
      onOptimisticPositionUpdate: props.onOptimisticPositionUpdate,
      onOptimisticPositionClear: props.onOptimisticPositionClear,
      onShiftSelect: props.onShiftSelect,
      onDeleteShift: props.onDeleteShift,
      selectedShiftIds: props.selectedShiftIds,
      actionsPanelMode: props.actionsPanelMode,
      onOpenActionsPanel: props.onOpenActionsPanel,
      onSetActionsPanelMode: props.onSetActionsPanelMode,
      isActionsPanelOpen: props.isActionsPanelOpen,
      onTemplateCreationClick: props.onTemplateCreationClick,
      onTemplateCreationHover: props.onTemplateCreationHover,
      onOpenInlineBuilder: props.onOpenInlineBuilder,
      isTemplateCreationInProgress: props.isTemplateCreationInProgress,
      onDayTemplateDrop: props.onDayTemplateDrop,
      templateCreationStartTime: props.templateCreationStartTime,
      templateCreationEndTime: props.templateCreationEndTime,
      templateCreationCursorTime: props.templateCreationCursorTime,
      templateCreationDayIndex: props.templateCreationDayIndex,
      templateCreationRowIndex: props.templateCreationRowIndex,
      isResizeMode: props.isResizeMode,
      onTemplateResizeComplete: props.onTemplateResizeComplete,
      onTemplateFinalizedClick: props.onTemplateFinalizedClick,
      checkIsHoliday: props.checkIsHoliday,
      currentTimeHours: props.currentTimeHours,
      currentTimeMinutes: props.currentTimeMinutes,
      getDisplayTimesForDay: props.getDisplayTimesForDay,
      getDayStatusByKey: props.getDayStatusByKey,
      showEvents: props.showEvents,
      eventPositionsWithLayout: props.eventPositionsWithLayout,
      eventsLookup: props.eventsLookup,
      scrollContainerRef: props.scrollContainerRef,
      // **CURSOR FOLLOW FIX**: Force new itemData reference when cursor time changes
      _cursorUpdateKey: props.templateCreationCursorTime?.getTime() ?? 0,
      // Historical edit mode - ensures HourCells re-render when mode is toggled
      isHistoricalEditEnabled: props.isHistoricalEditEnabled,
      // Hide status indicators (day borders)
      hideStatusIndicators: props.hideStatusIndicators,
      // Infinite scroll loading state
      isDayLoading: props.isDayLoading,
      };
    }, [
      dayRowCalculations,
      props.dayRowOffsets,
      props.shifts,
      props.shiftsByDay,
      props.shiftsByDayOrganized,
      props.employees,
      props.totalDisplayHours,
      props.effectiveDisplayStartTime?.getTime(), // Use timestamp for Date comparison
      props.effectiveDisplayEndTime?.getTime(),
      props.hourCellWidthPx,
      props.gridTemplateRows,
      props.gridTemplateColumns,
      props.businessId,
      props.countryCode,
      props.openingHoursByDayOfWeek,
      props.closedDatesMap,
      props.workingHours,
      props.hideDayColumn,
      props.hideActionsColumn,
      props.isEditingMode,
      props.show24Hours,
      props.expandedDayKeys,
      props.selectedEditingDate?.getTime(),
      props.onDaySelect,
      props.onAssignEmployee,
      props.onCreateShift,
      props.onRepositionShift,
      props.onOptimisticPositionUpdate,
      props.onOptimisticPositionClear,
      props.onShiftSelect,
      props.onDeleteShift,
      props.selectedShiftIds,
      props.actionsPanelMode,
      props.onOpenActionsPanel,
      props.onSetActionsPanelMode,
      props.isActionsPanelOpen,
      props.onTemplateCreationClick,
      props.onTemplateCreationHover,
      props.onOpenInlineBuilder,
      props.isTemplateCreationInProgress,
      props.onDayTemplateDrop,
      // **INTERACTION FIX**: Include all template creation state to ensure re-renders
      props.templateCreationStartTime?.getTime(),
      props.templateCreationEndTime?.getTime(),
      props.templateCreationCursorTime?.getTime(), // Critical for cursor-follow preview
      props.templateCreationDayIndex, // Critical for knowing which day to update
      props.templateCreationRowIndex, // Critical for positioning
      props.isResizeMode, // Critical for interaction mode
      props.onTemplateResizeComplete,
      props.onTemplateFinalizedClick,
      props.checkIsHoliday,
      props.currentTimeHours,
      props.currentTimeMinutes,
      props.getDisplayTimesForDay,
      props.getDayStatusByKey,
      props.showEvents,
      props.eventPositionsWithLayout,
      props.eventsLookup,
      props.scrollContainerRef,
      props.isHistoricalEditEnabled, // Re-render when historical edit mode changes
      props.hideStatusIndicators, // Re-render when status indicators visibility changes
      props.isDayLoading, // Re-render when loading callback changes
    ]);

    // Use virtualization utilities
    const {
      getItemSize,
      scrollToToday,
      scrollToDate,
      getCurrentDayIndex,
    } = useVirtualization({
      daysInSelectedMonth,
      dayRowCalculations,
      listRef,
    });

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      scrollToToday,
      scrollToDate,
      getCurrentDayIndex,
    }), [scrollToToday, scrollToDate, getCurrentDayIndex]);

    // Reset scroll cache when day row calculations change (height changes)
    // This handles both shift additions/deletions and any row count changes
    const previousNumRowsRef = useRef<number[]>([]);
    useEffect(() => {
      const currentNumRows = dayRowCalculations.map(calc => calc.numRows);
      const previousNumRows = previousNumRowsRef.current;

      // Check if heights actually changed
      let heightsChanged = currentNumRows.length !== previousNumRows.length;
      if (!heightsChanged) {
        for (let i = 0; i < currentNumRows.length; i++) {
          if (currentNumRows[i] !== previousNumRows[i]) {
            heightsChanged = true;
            break;
          }
        }
      }

      // Only reset if heights changed - force re-render of visible items
      if (heightsChanged && listRef.current) {
        listRef.current.resetAfterIndex(0, true);
      }

      previousNumRowsRef.current = currentNumRows;
    }, [dayRowCalculations]);


    // Calculate month boundary offsets for sticky month push effect
    const monthBoundaries = useMemo(() => {
      const boundaries: { index: number; offset: number; date: Date }[] = [];
      let cumulative = 0;

      for (let i = 0; i < dayRowCalculations.length; i++) {
        const dayCalc = dayRowCalculations[i];
        const prevDayCalc = i > 0 ? dayRowCalculations[i - 1] : null;

        // Add a boundary when month changes from previous day
        if (prevDayCalc && !isSameMonth(dayCalc.dayDate, prevDayCalc.dayDate)) {
          boundaries.push({
            index: i,
            offset: cumulative,
            date: dayCalc.dayDate
          });
        }

        // Accumulate height for this day (using getItemSize would be ideal but it's not available yet)
        // We need to calculate the same way getItemSize does
        cumulative += getItemSize(i);
      }

      return boundaries;
    }, [dayRowCalculations, getItemSize]);

    // Scroll tracking for iOS-style sticky month push effect
    const scrollOffsetRef = useRef(0);
    const rafRef = useRef<number | null>(null);

    // Effect to set up scroll listener for push offset calculation
    useEffect(() => {
      if (!onStickyMonthUpdate) return;

      // Access the outer scroll container from react-window
      const outer = (listRef.current as any)?._outerRef as HTMLElement;
      if (!outer) return;

      const handleScroll = () => {
        // Cancel any pending RAF
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
          const scrollTop = outer.scrollTop;
          scrollOffsetRef.current = scrollTop;

          // Find next month boundary after current scroll position
          const nextBoundary = monthBoundaries.find(b => b.offset > scrollTop);

          let offsetY = 0;
          if (nextBoundary) {
            // Calculate distance to where the actual line is, not the start of divider area
            const distanceToLine = (nextBoundary.offset + MONTH_DIVIDER_LINE_OFFSET_PX) - scrollTop;
            if (distanceToLine < MONTH_DIVIDER_VISUAL_HEIGHT_PX) {
              // Push current sticky up proportionally as next month line approaches
              offsetY = -(MONTH_DIVIDER_VISUAL_HEIGHT_PX - distanceToLine);
            }
          }

          onStickyMonthUpdate(offsetY);
        });
      };

      outer.addEventListener('scroll', handleScroll, { passive: true });

      // Initial calculation
      handleScroll();

      return () => {
        outer.removeEventListener('scroll', handleScroll);
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      };
    }, [onStickyMonthUpdate, monthBoundaries]);

    // Render function for each day item
    // CRITICAL: Depends on dayRowCalculations AND layout-critical props
    // itemProps is passed via react-window's itemData and accessed inside VirtualizedDayItem
    // Layout props ensure shifts recalculate when panel opens/closes or display changes

    const Row = useCallback(
      ({ index, style, data }: { index: number; style: React.CSSProperties; data: any }) => {
        const dayCalc = dayRowCalculations[index];
        const dayDate = dayCalc.dayDate;

        // Detect month boundary - show divider when month changes from previous day
        const prevDayCalc = index > 0 ? dayRowCalculations[index - 1] : null;
        const isFirstDayOfMonth = prevDayCalc && !isSameMonth(dayDate, prevDayCalc.dayDate);

        return (
          <div style={style}>
            {/* Month divider - shown when month changes */}
            {isFirstDayOfMonth && (
              <div
                className="month-divider absolute left-0 right-0 flex items-center pt-4 pb-4"
                style={{
                  height: `${MONTH_DIVIDER_HEIGHT_PX}px`,
                  zIndex: 5,
                }}
              >
                <div className="w-full border-t border-border" />
                {/* Month label aligned with Day column */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 bg-background px-1 flex flex-col items-center leading-tight" style={{ width: '32px' }}>
                  <span className="text-xs font-semibold">{format(dayDate, 'MMM')}</span>
                  <span className="text-[10px] text-muted-foreground">{format(dayDate, 'yyyy')}</span>
                </div>
              </div>
            )}
            {/* Day item - offset if there's a divider */}
            <div style={isFirstDayOfMonth ? { marginTop: `${MONTH_DIVIDER_HEIGHT_PX}px` } : undefined}>
              <VirtualizedDayItem
                key={`virtual-day-${index}`}
                index={index}
                style={{ ...style, position: 'relative', top: 0 }}
                dayDate={dayDate}
                dayCalc={dayCalc}
                itemData={data}
              />
            </div>
          </div>
        );
      },
      [
        dayRowCalculations,
        props.hourCellWidthPx,           // Changes when panel opens/closes
        props.totalDisplayHours,         // Changes when time range changes
        props.hideDayColumn,             // Changes column layout
        props.isActionsPanelOpen,        // Direct indicator of panel state
        props.effectiveDisplayStartTime?.getTime(),
        props.effectiveDisplayEndTime?.getTime(),
      ]
    );

    // Calculate total width based on grid template
    const totalWidth = useMemo(() => {
      // Let the container handle width naturally
      return '100%';
    }, []);

    // Let react-window handle scrolling natively - no artificial limits
    // This provides smoother UX than fighting the scroll with limits

    // Handle visible range changes for infinite scroll chunk loading and month indicator
    const handleItemsRendered = useCallback(
      ({ visibleStartIndex, visibleStopIndex }: { visibleStartIndex: number; visibleStopIndex: number }) => {
        onVisibleRangeChange?.(visibleStartIndex, visibleStopIndex);

        // Report visible month for sticky month indicator
        if (onVisibleMonthChange && dayRowCalculations[visibleStartIndex]) {
          const firstVisibleDay = dayRowCalculations[visibleStartIndex].dayDate;
          onVisibleMonthChange(startOfMonth(firstVisibleDay));
        }
      },
      [onVisibleRangeChange, onVisibleMonthChange, dayRowCalculations]
    );

    return (
      <VariableSizeList
        ref={listRef}
        height={containerHeight}
        itemCount={daysInSelectedMonth.length}
        itemSize={getItemSize}
        itemKey={(index) => {
          const dayCalc = dayRowCalculations[index];
          // Include numRows in key to force re-render when row count changes
          return `day-${dayCalc?.dayDate?.getTime() || index}-rows-${dayCalc?.numRows || 1}`;
        }}
        width={totalWidth}
        overscanCount={overscanCount}
        className="virtualized-calendar-list hide-scrollbar"
        itemData={itemProps}
        style={{ overflow: 'auto' }}
        onItemsRendered={(onVisibleRangeChange || onVisibleMonthChange) ? handleItemsRendered : undefined}
      >
        {Row}
      </VariableSizeList>
    );
  }
);

VirtualizedDayList.displayName = 'VirtualizedDayList';

// Wrap in memo with custom comparison to prevent unnecessary rerenders
export default React.memo(VirtualizedDayList, (prevProps, nextProps) => {
  // Core data changes that should trigger rerender
  if (
    prevProps.containerHeight !== nextProps.containerHeight ||
    prevProps.daysInSelectedMonth.length !== nextProps.daysInSelectedMonth.length ||
    prevProps.dayRowCalculations !== nextProps.dayRowCalculations ||
    prevProps.shifts !== nextProps.shifts ||
    prevProps.employees !== nextProps.employees ||
    prevProps.totalDisplayHours !== nextProps.totalDisplayHours ||
    prevProps.hourCellWidthPx !== nextProps.hourCellWidthPx
  ) {
    return false; // Props changed, rerender
  }

  // Display time changes
  if (
    prevProps.effectiveDisplayStartTime?.getTime() !== nextProps.effectiveDisplayStartTime?.getTime() ||
    prevProps.effectiveDisplayEndTime?.getTime() !== nextProps.effectiveDisplayEndTime?.getTime()
  ) {
    return false; // Time range changed, rerender
  }

  // UI state changes
  if (
    prevProps.hideDayColumn !== nextProps.hideDayColumn ||
    prevProps.show24Hours !== nextProps.show24Hours ||
    prevProps.isActionsPanelOpen !== nextProps.isActionsPanelOpen ||
    prevProps.actionsPanelMode !== nextProps.actionsPanelMode ||
    prevProps.isHistoricalEditEnabled !== nextProps.isHistoricalEditEnabled
  ) {
    return false; // UI config changed, rerender
  }

  // Template creation state changes
  if (
    prevProps.isTemplateCreationInProgress !== nextProps.isTemplateCreationInProgress ||
    prevProps.templateCreationStartTime?.getTime() !== nextProps.templateCreationStartTime?.getTime() ||
    prevProps.templateCreationEndTime?.getTime() !== nextProps.templateCreationEndTime?.getTime() ||
    prevProps.templateCreationDayIndex !== nextProps.templateCreationDayIndex ||
    prevProps.templateCreationRowIndex !== nextProps.templateCreationRowIndex
  ) {
    return false; // Template state changed, rerender
  }

  // Current time changes (for red line) - only check if values actually changed
  if (
    prevProps.currentTimeHours !== nextProps.currentTimeHours ||
    prevProps.currentTimeMinutes !== nextProps.currentTimeMinutes
  ) {
    return false; // Current time changed, rerender
  }

  // Selection state
  if (prevProps.selectedShiftIds !== nextProps.selectedShiftIds) {
    return false; // Selection changed, rerender
  }

  // Selected editing date (for day actions)
  if (prevProps.selectedEditingDate?.getTime() !== nextProps.selectedEditingDate?.getTime()) {
    return false; // Selected day changed, rerender
  }

  // Expanded days
  if (prevProps.expandedDayKeys !== nextProps.expandedDayKeys) {
    return false; // Expanded days changed, rerender
  }

  // Don't rerender for callback reference changes - they're stable via useCallback in parent
  return true; // Skip rerender
});